import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useSubscription } from '../contexts/SubscriptionContext';

export const EarlyAccessWelcome: React.FC = () => {
  const { createEarlyAccessSubscription, loading } = useSubscription();
  const [acknowledged, setAcknowledged] = useState({
    earlyAccess: false,
    aiLimitations: false
  });
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const scrollToButton = (): void => {
    if (buttonRef.current && 'scrollIntoView' in buttonRef.current) {
      const element = buttonRef.current as HTMLElement;
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleCheckboxChange = (field: 'earlyAccess' | 'aiLimitations', checked: boolean): void => {
    setAcknowledged(prev => ({
      ...prev,
      [field]: checked
    }));
    
    // Small delay to let the state update, then scroll to button
    setTimeout(() => {
      scrollToButton();
    }, 100);
  };

  const handleCreateSubscription = async (): Promise<void> => {
    try {
      setError(null);
      await createEarlyAccessSubscription();
      // Subscription context will update automatically
    } catch (error) {
      setError('Failed to set up your early access. Please try again.');
      console.error('Early access subscription creation failed:', error);
    }
  };

  const canProceed = acknowledged.earlyAccess && acknowledged.aiLimitations;

  return (
    <Box sx={{ 
      height: '100vh',
      width: '100vw',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      p: 2,
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <Paper 
        elevation={8} 
        sx={{ 
          p: 4, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: 700,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Logo */}
          <Box
            component="img"
            src={new URL('../assets/gekkovet-logo.png', import.meta.url).href}
            alt="GekkoVet Logo"
            sx={{ 
              height: 80,
              width: 'auto',
              objectFit: 'contain',
              mb: 2
            }}
          />
          
          {/* Title */}
          <Box textAlign="center">
            <Typography variant="h3" component="h1" gutterBottom fontWeight="bold" color="primary">
              Welcome to GekkoVet Copilot
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 2,
              display: 'inline-flex'
            }}>
              <RocketIcon sx={{ color: 'white' }} />
              <Typography variant="h5" component="h2" sx={{ color: 'white', fontWeight: 600 }}>
                Early Access
              </Typography>
            </Stack>
          </Box>
          
          {/* Welcome Message */}
          <Box sx={{ textAlign: 'left', width: '100%' }}>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
              Thank you for your interest in GekkoVet Copilot! We&apos;re excited to have you 
              as one of our early access users.
            </Typography>
            
            <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
              What is Early Access?
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ mb: 2 }}>
              GekkoVet Copilot is currently in <strong>early access mode</strong>, which means:
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="info" /></ListItemIcon>
                <ListItemText primary="The AI is still being refined and improved" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="info" /></ListItemIcon>
                <ListItemText primary="You may encounter occasional inaccuracies or limitations" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="info" /></ListItemIcon>
                <ListItemText primary="Your feedback helps us make the product better" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography component="span">
                      Access is completely <strong>free during early access</strong>
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>

          {/* Acknowledgments */}
          <Box sx={{ width: '100%' }}>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged.earlyAccess}
                    onChange={(e) => handleCheckboxChange('earlyAccess', Boolean(e.target.checked))}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I understand that GekkoVet Copilot is in early access mode and 
                    I will get to use it for free during this period.
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged.aiLimitations}
                    onChange={(e) => handleCheckboxChange('aiLimitations', Boolean(e.target.checked))}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I understand that this is an AI product that may make mistakes, 
                    and I will use my professional judgment when applying any recommendations.
                  </Typography>
                }
              />
            </Stack>
          </Box>

          {/* Important Notice */}
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ width: '100%' }}
          >
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Important Veterinary Disclaimer
            </Typography>
            <Typography variant="body2">
              GekkoVet Copilot is designed to assist veterinary professionals and should 
              not replace professional veterinary judgment. Always verify recommendations 
              and use your clinical expertise when making treatment decisions.
            </Typography>
          </Alert>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          {/* Proceed Button */}
          <Button
            ref={buttonRef}
            variant="contained"
            size="large"
            onClick={handleCreateSubscription}
            disabled={!canProceed || loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RocketIcon />}
            sx={{ 
              py: 1.5, 
              px: 4, 
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'translateY(-2px)',
                boxShadow: 6,
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {loading ? 'Setting up your access...' : 'Start Using GekkoVet Copilot'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
